/**
 * IssuesBlock — solo las incidencias que cambian la operación de hoy.
 *
 * Se ordenan por lo que duele: primero las que bloquean disponibilidad,
 * después por urgencia. Por defecto se ven tres; el resto está a un toque y el
 * detalle completo, en el módulo de Incidencias.
 *
 * Bloquear disponibilidad no es una etiqueta: significa que existe un bloqueo
 * de mantenimiento sobre la unidad y que, cuando haya channel manager, ese
 * cierre viaja hacia fuera (§8.2, §13). Por eso se dice hasta cuándo.
 */
import React, { useState } from "react";
import { ISSUE_STATUS_LABEL, URGENCY_LABEL } from "@/data/todayV2Demo";
import { sortIssuesByImpact } from "@/lib/todayV2Selectors";
import {
  TodayBlock, TodayCard, RowList, Row, Meta, StatusPill, QuickAction,
  EmptyState, ShowMore,
} from "@/pages/admin/pms/v2/V2Primitives";

const STATUS_TONE = {
  OPEN: "amber",
  ASSIGNED: "blue",
  IN_PROGRESS: "cyan",
  RESOLVED: "emerald",
  INSPECTED: "stone",
};

const PRIORITY_TONE = { urgent: "red", high: "red", medium: "amber", low: "stone" };
const VISIBLE_BY_DEFAULT = 3;

export default function IssuesBlock({ issues, board }) {
  const [expanded, setExpanded] = useState(false);
  const ordered = sortIssuesByImpact(issues);
  const visible = expanded ? ordered : ordered.slice(0, VISIBLE_BY_DEFAULT);
  const hasMore = ordered.length > visible.length;

  return (
    <TodayBlock
      id="today-issues"
      title="Incidencias"
      count={ordered.length}
      testId="today-issues"
    >
      {ordered.length === 0 ? (
        <EmptyState testId="today-issues-empty">Sin incidencias abiertas.</EmptyState>
      ) : (
        <TodayCard padding="p-0">
          <RowList testId="today-issues-list">
            {visible.map((issue) => (
              <IssueRow
                key={issue.issue_id}
                issue={issue}
                block={maintenanceBlockFor(board, issue.villa_id)}
              />
            ))}
          </RowList>
        </TodayCard>
      )}

      {(hasMore || expanded) && (
        <ShowMore
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          moreLabel={`Ver las ${ordered.length} incidencias`}
          testId="today-issues-more"
        />
      )}
    </TodayBlock>
  );
}

/** Solo el bloqueo de mantenimiento explica una incidencia bloqueante. */
function maintenanceBlockFor(board, unitId) {
  const row = board.find((b) => b.unit.villa_id === unitId);
  return row?.block?.type === "MAINTENANCE" ? row.block : null;
}

function IssueRow({ issue, block }) {
  const blocking = Boolean(issue.blocks_inventory);
  return (
    <Row testId={`issue-${issue.issue_id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100">
            {issue.villa_id}
            <span className="text-stone-400"> · </span>
            {issue.title}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <StatusPill tone={PRIORITY_TONE[issue.priority] || "stone"} dot={false}>
              {URGENCY_LABEL[issue.priority] || issue.priority}
            </StatusPill>
            {blocking && (
              <StatusPill tone="red" dot={false} testId={`issue-blocking-${issue.issue_id}`}>
                Bloquea disponibilidad
                {block ? ` hasta el ${block.end_date.slice(8)}` : ""}
              </StatusPill>
            )}
            <StatusPill tone={STATUS_TONE[issue.status_v2]} testId={`issue-status-${issue.issue_id}`}>
              {ISSUE_STATUS_LABEL[issue.status_v2]}
            </StatusPill>
          </div>

          <Meta items={[
            issue.assignee ? `Asignada a ${issue.assignee.name}` : "Sin asignar",
            issue.issue_id,
          ]} className="mt-0.5" />
        </div>

        <div className="shrink-0">
          <QuickAction
            label="Abrir"
            to="/pms/issues"
            primary={blocking}
            testId={`issue-open-${issue.issue_id}`}
          />
        </div>
      </div>
    </Row>
  );
}
